import { ButtonContent } from '../../ui/ButtonContent';
/**
 * Members management section for the settings page.
 *
 * ─── Data flow ───────────────────────────────────────────────────────────
 * Invite CRUD (create / list / resend / revoke) goes through the
 * `useInvites` hook → Pages Function proxies under `/api/organizations/…`
 * → metering-svc. The hook throws named errors (`SeatLimitExceededError`,
 * `ResendCooldownError`, …) that this component maps to user-facing copy.
 *
 * Member listing and removal still use Supabase RLS directly - server-side
 * RPCs for those are out of scope for C2.
 *
 * Seat bar: billable = members + pending invites. On paid tiers, viewer
 * members are excluded from the billable count (enforced server-side; this
 * is just a consistent local representation).
 *
 * @module components/admin/settings/MembersSection
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOrg } from '../../../lib/OrgProvider';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { useInvites, type Invite, InviteApiError } from '../../../lib/useInvites';
import { useEntitlements } from '../../../lib/useEntitlements';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import type { OrgRole } from '../../../lib/org-types';
import {
  IconUsersGroup,
  IconTrash,
  IconMail,
} from '@tabler/icons-react';
import { PlusIcon } from '@/components/icons';

/**
 * Member row with resolved email + display name.
 *
 * Emails come from the SECURITY DEFINER RPC `get_org_members_with_emails`
 * so authenticated clients can render real addresses instead of UUIDs
 * without ever touching `auth.users` directly.
 */
interface MemberRow {
  user_id: string;
  /** FK into `org_roles`. Added so the role-switch dropdown can render the
   *  current selection and resolve option names -> ids on change. */
  role_id: string;
  role_name: string;
  email: string;
  full_name: string | null;
  joined_at: string;
}

/**
 * Raw row shape returned by the `get_org_members_with_emails` RPC. Fields are
 * declared nullable to mirror the loosely-typed RPC result before it is
 * normalized into a {@link MemberRow}.
 */
interface MemberEmailRpcRow {
  user_id: string;
  role_id: string | null;
  role_name: string | null;
  email: string | null;
  full_name: string | null;
  joined_at: string;
}

/** Maximum number of resends permitted per invite (mirrors metering-svc policy). */
const RESEND_CAP = 5;

/** Context for invite recovery, used to keep uncertain outcomes action-specific. */
type InviteErrorContext = 'create' | 'cancel' | 'resend';

type RecoveryAction = 'refresh-invites' | 'reload-members';
type BlockedAction = 'send' | 'resend' | 'cancel';

interface RecoveryMessage {
  message: string;
  action?: RecoveryAction;
  blockedAction?: BlockedAction;
  retryAfterMs?: number;
}

interface RetryAfter {
  label: string;
  milliseconds: number;
}

/** Largest delay that browsers can schedule without overflowing setTimeout. */
const MAX_TIMER_DELAY_SECONDS = Math.floor(2_147_483_647 / 1_000);

/**
 * Map an invite failure to an outcome and the safest next action. Unknown
 * mutation outcomes are never presented as confirmed failures because the
 * server may have completed the action before the response was lost.
 *
 * @param err   Upstream error thrown by `useInvites`.
 * @param context Which user action raised the error. Governs the fallback copy
 *   when the error code is unknown (so a revoke failure doesn't read "Failed
 *   to send invite").
 */
function inviteErrorPresentation(
  err: unknown,
  context: InviteErrorContext = 'create',
): RecoveryMessage {
  const fallback: Record<InviteErrorContext, RecoveryMessage> = {
    create: {
      message: 'The invite may have been sent. Check pending invites before sending it again.',
      action: 'refresh-invites',
      blockedAction: 'send',
    },
    cancel: {
      message: 'The invite may already be canceled. Refresh the list before trying again.',
      action: 'refresh-invites',
    },
    resend: {
      message: 'The email may have been sent again. Refresh the list before trying again.',
      action: 'refresh-invites',
    },
  };
  if (err instanceof InviteApiError) {
    switch (err.name) {
      case 'SeatLimitExceededError':
        return {
          message: 'All member seats are in use. Remove a member or cancel a pending invite before sending another.',
        };
      case 'DuplicatePendingInviteError':
        return {
          message: 'An invite is already pending for this email. Use Resend on the existing invite.',
        };
      case 'EmailAlreadyMemberError':
        return { message: 'This person is already a member.' };
      case 'InviteRateLimitHourError':
      case 'InviteRateLimitDayError': {
        const detail = err.detail && typeof err.detail === 'object'
          ? err.detail as Record<string, unknown>
          : null;
        const retryAfter = parseRetryAfter(detail?.retry_after_seconds);
        return {
          message: retryAfter
            ? `Invite sending is paused. Try again in ${retryAfter.label}.`
            : 'Invite sending is paused for now. Try again later.',
          retryAfterMs: retryAfter?.milliseconds,
        };
      }
      case 'OrgPendingInvitesCapError':
        return {
          message: 'There are too many pending invites. Cancel one before sending another.',
        };
      case 'ResendCooldownError': {
        const detail = err.detail && typeof err.detail === 'object'
          ? err.detail as Record<string, unknown>
          : null;
        const retryAfter = parseRetryAfter(detail?.retry_after_seconds);
        return {
          message: retryAfter
            ? `This email was sent recently. Try again in ${retryAfter.label}.`
            : 'This email was sent recently. Try again later.',
          retryAfterMs: retryAfter?.milliseconds,
        };
      }
      case 'ResendCapReachedError':
        return {
          message: 'This invite has reached its resend limit. Cancel it, then send a new invite.',
          blockedAction: 'resend',
        };
      case 'InviteNotPendingError':
        return {
          message: 'This invite is no longer pending. Refresh the list to see its current status.',
          action: 'refresh-invites',
        };
      case 'ForbiddenError':
        return {
          message: context === 'cancel'
          ? 'You no longer have permission to cancel this invite.'
          : context === 'resend'
            ? 'You no longer have permission to resend this invite.'
            : 'You no longer have permission to send invites.',
          blockedAction: context === 'cancel'
            ? 'cancel'
            : context === 'resend'
              ? 'resend'
              : 'send',
        };
      default:
        return fallback[context];
    }
  }
  return fallback[context];
}

/** Validate and format a server-provided delay for matching copy and control state. */
function parseRetryAfter(value: unknown): RetryAfter | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  const seconds = Math.ceil(value);
  if (seconds > MAX_TIMER_DELAY_SECONDS) return null;
  const milliseconds = seconds * 1_000;
  if (seconds < 60) {
    return {
      label: `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`,
      milliseconds,
    };
  }
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) {
    return {
      label: `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`,
      milliseconds,
    };
  }
  const hours = Math.ceil(minutes / 60);
  return {
    label: `${hours} ${hours === 1 ? 'hour' : 'hours'}`,
    milliseconds,
  };
}

/** Render a short "Sent 5 min ago" style string, falling back to a full date. */
function relativeSent(iso: string | null): string {
  if (!iso) return 'not yet sent';
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'Sent just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Sent ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Sent ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Sent ${days}d ago`;
  return `Sent ${new Date(iso).toLocaleDateString()}`;
}

/**
 * Members management section.
 *
 * Lists current members, shows seat usage, supports inviting / resending /
 * revoking through `useInvites`, and removing members via Supabase RLS.
 */
export const MembersSection: React.FC = () => {
  const { activeOrg, orgBilling, userRole } = useOrg();
  const { supabase, user, retrySession } = useSupabase();
  const { tier } = useEntitlements();
  const invitesApi = useInvites(activeOrg?.id ?? null);

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [roles, setRoles] = useState<OrgRole[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);

  const activeOrgId = activeOrg?.id ?? null;
  const activeOrgIdRef = useRef(activeOrgId);
  activeOrgIdRef.current = activeOrgId;
  const membersOrgIdRef = useRef<string | null>(null);
  const memberLoadGenerationRef = useRef(0);

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviteRoleName, setInviteRoleName] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<RecoveryMessage | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  // Per-row resend/cancel state
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, RecoveryMessage | undefined>>({});
  const [checkingSignIn, setCheckingSignIn] = useState(false);
  const [interactionOrgId, setInteractionOrgId] = useState<string | null>(activeOrgId);
  const inviteRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRetryTimerRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clearInviteRetryTimer = useCallback(() => {
    if (inviteRetryTimerRef.current === null) return;
    clearTimeout(inviteRetryTimerRef.current);
    inviteRetryTimerRef.current = null;
  }, []);

  const clearRowRetryTimer = useCallback((inviteId: string) => {
    const timer = rowRetryTimerRefs.current.get(inviteId);
    if (timer === undefined) return;
    clearTimeout(timer);
    rowRetryTimerRefs.current.delete(inviteId);
  }, []);

  const clearAllRetryTimers = useCallback(() => {
    clearInviteRetryTimer();
    rowRetryTimerRefs.current.forEach((timer) => clearTimeout(timer));
    rowRetryTimerRefs.current.clear();
  }, [clearInviteRetryTimer]);

  /**
   * Tracks whether the invite-role default has already been set. Using a ref
   * avoids the stale-closure bug where `loadMembers` captures an empty
   * `inviteRoleId` at creation time and resets the selection on every re-run.
   */
  const inviteRoleInitializedRef = useRef(false);

  const canManage = userRole === 'owner' || userRole === 'admin';
  const maxSeats = orgBilling?.max_seats ?? 5;
  const membersBelongToActiveOrg = membersOrgIdRef.current === activeOrgId;
  const activeMembers = membersBelongToActiveOrg ? members : [];
  const activeMembersError = membersBelongToActiveOrg ? membersError : null;
  const activeMembersLoading = activeOrgId !== null
    && (!membersBelongToActiveOrg || loadingMembers);
  const interactionBelongsToActiveOrg = interactionOrgId === activeOrgId;
  const activeShowInviteForm = interactionBelongsToActiveOrg && showInviteForm;
  const activeInviteError = interactionBelongsToActiveOrg ? inviteError : null;
  const activeInviteSuccess = interactionBelongsToActiveOrg ? inviteSuccess : null;
  const activeInviting = interactionBelongsToActiveOrg && inviting;
  const activeResendingId = interactionBelongsToActiveOrg ? resendingId : null;
  const activeCancelingId = interactionBelongsToActiveOrg ? cancelingId : null;
  const activeRowError = interactionBelongsToActiveOrg ? rowError : {};

  /**
   * Compute billable seats: members + pending invites.
   *
   * On paid tiers, viewers are unlimited - so both viewer *members* and
   * pending *viewer invites* are excluded from the count. Excluding one
   * without the other would let an admin exceed the cap by "parking"
   * viewers in a different state.
   *
   * Free tier counts everyone, which matches the server-side metering rules.
   */
  const billableMembers = tier === 'free'
    ? activeMembers.length
    : activeMembers.filter((m) => m.role_name !== 'viewer').length;
  const billableInvites = tier === 'free'
    ? invitesApi.invites.length
    : invitesApi.invites.filter((i) => i.role_name !== 'viewer').length;
  const billableSeats = billableMembers + billableInvites;

  /**
   * Gate the Send button on the seat cap - but a paid-tier viewer invite
   * bypasses the cap entirely, since viewers don't consume seats. The
   * selected role has to be part of the decision, not just the current
   * billable count.
   */
  const invitingUnlimitedViewer = tier !== 'free' && inviteRoleName === 'viewer';
  const canInvite = invitingUnlimitedViewer || billableSeats < maxSeats;

  /**
   * Hide pending invites for users who are already members (ghost-invite
   * scenario - accept flow half-completed, leaving an orphan pending row).
   * Purely cosmetic: the invite row still exists server-side and server-side
   * rate limits still count against it.
   */
  const memberEmailsLower = new Set(activeMembers.map((m) => m.email.toLowerCase()));
  const displayedInvites = invitesApi.invites.filter(
    (inv) => !memberEmailsLower.has(inv.email.toLowerCase()),
  );

  /**
   * Pre-gate the Send button when the typed email already belongs to a
   * member. The server still raises `EmailAlreadyMemberError` as the
   * authoritative check, but blocking client-side avoids the round-trip
   * and gives an immediate explanation while the admin is still typing.
   */
  const trimmedInviteEmail = inviteEmail.trim().toLowerCase();
  const inviteEmailMatchesMember =
    trimmedInviteEmail.length > 0 && memberEmailsLower.has(trimmedInviteEmail);
  const inviteSendGuidance = [
    !canInvite ? 'invite-member-limit' : null,
    inviteEmailMatchesMember ? 'invite-existing-member' : null,
    activeInviteError ? 'invite-action-error' : null,
  ].filter((id): id is string => id !== null).join(' ') || undefined;

  /** Fetch members and roles for the active org (invites fetched via useInvites). */
  const loadMembers = useCallback(async () => {
    const loadOrgId = activeOrg?.id ?? null;
    if (activeOrgIdRef.current !== loadOrgId) return;
    const loadGeneration = ++memberLoadGenerationRef.current;
    if (!loadOrgId) {
      membersOrgIdRef.current = null;
      setMembers([]);
      setRoles([]);
      setMembersError(null);
      setLoadingMembers(false);
      return;
    }
    if (membersOrgIdRef.current !== loadOrgId) {
      membersOrgIdRef.current = loadOrgId;
      setMembers([]);
      setRoles([]);
    }
    setLoadingMembers(true);
    setMembersError(null);

    try {
      // RPC returns real emails + display names via SECURITY DEFINER + membership
      // check, so non-members can't enumerate and authed clients don't need
      // direct auth.users access.
      const { data: memberData, error: memberErr } = await supabase.rpc(
        'get_org_members_with_emails',
        { p_org_id: loadOrgId },
      );

      if (memberErr) {
        throw new Error('Member RPC failed');
      }

      const memberRows: MemberRow[] = ((memberData ?? []) as MemberEmailRpcRow[]).map((m) => ({
        user_id: m.user_id,
        role_id: m.role_id ?? '',
        role_name: m.role_name ?? 'unknown',
        email: m.email ?? '',
        full_name: (m.full_name ?? '').trim() || null,
        joined_at: m.joined_at,
      }));

      const { data: roleData } = await supabase
        .from('org_roles')
        .select('*')
        .eq('is_system', true);

      if (
        loadGeneration !== memberLoadGenerationRef.current
        || activeOrgIdRef.current !== loadOrgId
      ) return;

      const systemRoles = (roleData ?? []) as OrgRole[];
      membersOrgIdRef.current = loadOrgId;
      setMembers(memberRows);
      setRoles(systemRoles);
      // Only set the invite-role default once: the first time roles load. Use a
      // ref so subsequent loadMembers re-runs (e.g. after a role change) do not
      // reset a selection the admin already made in the invite form.
      if (systemRoles.length > 0 && !inviteRoleInitializedRef.current) {
        inviteRoleInitializedRef.current = true;
        const editorRole = systemRoles.find((r) => r.name === 'editor');
        setInviteRoleId(editorRole?.id ?? systemRoles[0].id);
        setInviteRoleName(editorRole?.name ?? systemRoles[0].name);
      }
    } catch {
      if (
        loadGeneration !== memberLoadGenerationRef.current
        || activeOrgIdRef.current !== loadOrgId
      ) return;
      captureException(
        new Error('Members load failed'),
        'MembersSection.loadMembers',
        { orgId: loadOrgId },
      );
      setMembersError("Couldn't load members.");
    } finally {
      if (
        loadGeneration === memberLoadGenerationRef.current
        && activeOrgIdRef.current === loadOrgId
      ) {
        setLoadingMembers(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg?.id, supabase]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    clearAllRetryTimers();
    setInteractionOrgId(activeOrgId);
    setShowInviteForm(false);
    setInviteEmail('');
    setInviting(false);
    setInviteError(null);
    setInviteSuccess(null);
    setResendingId(null);
    setCancelingId(null);
    setRowError({});
    setCheckingSignIn(false);
    return clearAllRetryTimers;
  }, [activeOrgId, clearAllRetryTimers]);

  /** Send an invite via the new metering-svc API. */
  const handleInvite = async () => {
    if (!activeOrg?.id || !inviteEmail) return;
    const operationOrgId = activeOrg.id;

    setInviting(true);
    clearInviteRetryTimer();
    setInviteError(null);
    setInviteSuccess(null);

    try {
      const result = await invitesApi.create({
        email: inviteEmail.toLowerCase().trim(),
        role: inviteRoleName,
      });
      if (activeOrgIdRef.current !== operationOrgId) return;
      // The backend returns `sent: boolean` indicating whether the Brevo
      // transactional email actually went out. When `sent === false` the
      // invite row exists (and counts toward seats) but the admin must retry
      // the email via the Resend button on the pending-invite row.
      if (result.sent === false) {
        setInviteSuccess("Invite created, but the email wasn't sent. Use Resend on the pending invite.");
      } else {
        setInviteSuccess(`Invite sent to ${inviteEmail}`);
      }
      setInviteEmail('');
      setShowInviteForm(false);
    } catch (err) {
      if (activeOrgIdRef.current === operationOrgId) {
        const recovery = inviteErrorPresentation(err);
        setInviteError(recovery);
        if (recovery.retryAfterMs !== undefined) {
          inviteRetryTimerRef.current = setTimeout(() => {
            inviteRetryTimerRef.current = null;
            setInviteError((current) => current === recovery ? null : current);
          }, recovery.retryAfterMs);
        }
      }
    } finally {
      if (activeOrgIdRef.current === operationOrgId) setInviting(false);
    }
  };

  /** Resend a pending invite. Errors map to resend-scoped copy (per row). */
  const handleResend = async (inviteId: string) => {
    if (!activeOrg?.id) return;
    const operationOrgId = activeOrg.id;
    setResendingId(inviteId);
    clearRowRetryTimer(inviteId);
    setRowError((prev) => ({ ...prev, [inviteId]: undefined }));
    try {
      await invitesApi.resend(inviteId);
    } catch (err) {
      if (activeOrgIdRef.current === operationOrgId) {
        const recovery = inviteErrorPresentation(err, 'resend');
        setRowError((prev) => ({
          ...prev,
          [inviteId]: recovery,
        }));
        if (recovery.retryAfterMs !== undefined) {
          const timer = setTimeout(() => {
            if (rowRetryTimerRefs.current.get(inviteId) === timer) {
              rowRetryTimerRefs.current.delete(inviteId);
            }
            setRowError((current) => {
              if (current[inviteId] !== recovery) return current;
              const next = { ...current };
              delete next[inviteId];
              return next;
            });
          }, recovery.retryAfterMs);
          rowRetryTimerRefs.current.set(inviteId, timer);
        }
      }
    } finally {
      if (activeOrgIdRef.current === operationOrgId) setResendingId(null);
    }
  };

  /**
   * Cancel a pending invite. Errors are scoped to the row and uncertain
   * outcomes direct the admin to refresh before attempting another mutation.
   */
  const handleRevokeInvite = async (inviteId: string) => {
    if (!activeOrg?.id) return;
    const operationOrgId = activeOrg.id;
    setCancelingId(inviteId);
    setRowError((prev) => ({ ...prev, [inviteId]: undefined }));
    try {
      await invitesApi.revoke(inviteId);
    } catch (err) {
      if (activeOrgIdRef.current === operationOrgId) {
        setRowError((prev) => ({
          ...prev,
          [inviteId]: inviteErrorPresentation(err, 'cancel'),
        }));
      }
    } finally {
      if (activeOrgIdRef.current === operationOrgId) setCancelingId(null);
    }
  };

  /**
   * Change an existing member's role. The `org_members` UPDATE RLS policy
   * requires `members:change_roles` (owner + admin only); the UI simply
   * mirrors that gate. Owners can't be demoted through this flow - the DB
   * also has a last-owner trigger as a safety net.
   */
  const handleChangeRole = async (
    userId: string,
    targetRoleId: string,
    previousRoleName: string,
  ) => {
    if (!activeOrg?.id) return;
    const operationOrgId = activeOrg.id;
    // Clear any prior inline error for this row.
    setRowError((prev) => ({ ...prev, [`member-${userId}`]: undefined }));

    try {
      const { error: updateErr } = await supabase
        .from('org_members')
        .update({ role_id: targetRoleId })
        .eq('organization_id', operationOrgId)
        .eq('user_id', userId);

      if (updateErr) {
        throw new Error('Member role update failed');
      }

      if (activeOrgIdRef.current !== operationOrgId) return;
      // Optimistically refresh to reflect the new role_name in the list.
      void loadMembers();
    } catch {
      if (activeOrgIdRef.current !== operationOrgId) return;
      captureException(
        new Error('Member role update failed'),
        'MembersSection.handleChangeRole',
        { orgId: operationOrgId, userId, targetRoleId, previousRoleName },
      );
      setRowError((prev) => ({
        ...prev,
        [`member-${userId}`]: {
          message: 'The role may have changed. Refresh members before changing it again.',
          action: 'reload-members',
        },
      }));
      // Revert the dropdown by re-running the fetch (role_name from RPC wins).
      void loadMembers();
    }
  };

  /**
   * Remove a member from the org by `(organization_id, user_id)`.
   *
   * The previous implementation keyed by the `org_members` row id, which the
   * members RPC doesn't return. `(organization_id, user_id)` is unique by
   * schema constraint so it's an equally safe delete target. RLS on
   * `org_members` still enforces `members:remove` permission server-side.
   *
   * Also tidies up the historical invite trail for that email: any
   * accepted / revoked invite rows for the removed user's email on this org
   * are deleted so the admin never sees rubble later. RLS on `org_invites`
   * gates DELETE on `members:invite` permission (owner + admin), which
   * anyone removing a member already has. The invite cleanup is best-effort
   * - if it fails, the member-remove has already succeeded; we log and
   * move on. A future re-invite works cleanly either way thanks to the
   * partial-unique-on-pending index.
   */
  const handleRemoveMember = async (userId: string) => {
    if (!activeOrg?.id) return;
    const operationOrgId = activeOrg.id;

    // Capture the email NOW - once the org_members row is gone, the RPC
    // won't return this user anymore so we can't look it up.
    const memberEmail = activeMembers.find((m) => m.user_id === userId)?.email ?? '';

    try {
      const { error: deleteErr } = await supabase
        .from('org_members')
        .delete()
        .eq('organization_id', operationOrgId)
        .eq('user_id', userId);

      if (deleteErr) {
        throw new Error('Member removal failed');
      }

      if (memberEmail) {
        const { error: inviteCleanupErr } = await supabase
          .from('org_invites')
          .delete()
          .eq('organization_id', operationOrgId)
          .ilike('email', memberEmail)
          .in('status', ['accepted', 'revoked']);

        if (inviteCleanupErr) {
          if (activeOrgIdRef.current === operationOrgId) {
            captureException(
              new Error('Removed member invite cleanup failed'),
              'MembersSection.handleRemoveMember.inviteCleanup',
              { orgId: operationOrgId, userId },
            );
          }
        }
      }

      if (activeOrgIdRef.current !== operationOrgId) return;
      void loadMembers();
      void invitesApi.refresh();
    } catch {
      if (activeOrgIdRef.current !== operationOrgId) return;
      captureException(
        new Error('Member removal failed'),
        'MembersSection.handleRemoveMember',
        { orgId: operationOrgId, userId },
      );
      setRowError((prev) => ({
        ...prev,
        [`member-${userId}`]: {
          message: 'The member may already have been removed. Refresh members before trying again.',
          action: 'reload-members',
        },
      }));
    }
  };

  const refreshInvitesAfterError = (rowId?: string) => {
    if (rowId) {
      setRowError((prev) => ({ ...prev, [rowId]: undefined }));
    } else {
      setInviteError(null);
    }
    void invitesApi.refresh();
  };

  const reloadMembersAfterError = (rowId?: string) => {
    if (rowId) {
      setRowError((prev) => ({ ...prev, [rowId]: undefined }));
    }
    void loadMembers();
  };

  const checkSignInAndRefreshInvites = async () => {
    const operationOrgId = activeOrgId;
    setCheckingSignIn(true);
    try {
      await retrySession();
      if (activeOrgIdRef.current === operationOrgId) {
        await invitesApi.refresh();
      }
    } finally {
      if (activeOrgIdRef.current === operationOrgId) setCheckingSignIn(false);
    }
  };

  if (!activeOrg) {
    return <div className="text-muted-foreground text-sm">No organization selected.</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <IconUsersGroup className="w-6 h-6 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Members</h2>
        </div>
        <div className="text-sm text-muted-foreground">
          {billableSeats}/{maxSeats} seats used
        </div>
      </div>

      {/* Seat usage bar */}
      <div className="mb-2">
        <div className="w-full bg-surface rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              billableSeats >= maxSeats ? 'bg-danger' : 'bg-volt'
            }`}
            style={{ width: `${Math.min((billableSeats / maxSeats) * 100, 100)}%` }}
          />
        </div>
      </div>
      {tier !== 'free' && (
        <p className="text-xs text-muted-foreground mb-6">
          Viewers don't count toward your seat limit.
        </p>
      )}
      {tier === 'free' && <div className="mb-6" />}

      {invitesApi.error && (
        <div
          role="alert"
          className="mb-6 flex items-center justify-between gap-3 rounded-lg bg-wash-danger px-3 py-2 text-sm text-danger-soft"
        >
          <span>
            {invitesApi.errorKind === 'reauth'
              ? "We need to check your sign-in again."
              : invitesApi.errorKind === 'forbidden'
                ? "You don't have access to pending invites."
                : invitesApi.invites.length > 0
                  ? "Couldn't refresh pending invites. The list may be out of date."
                  : "Couldn't load pending invites. Refresh the list before sending an invite."}
          </span>
          {invitesApi.errorKind === 'reauth' ? (
            <button aria-busy={checkingSignIn}
              type="button"
              onClick={() => void checkSignInAndRefreshInvites()}
              disabled={checkingSignIn || invitesApi.loading}
              className="shrink-0 font-medium text-danger-soft hover:opacity-80 disabled:opacity-50"
            >
              <ButtonContent loading={checkingSignIn}>Check sign-in</ButtonContent>
            </button>
          ) : invitesApi.errorKind !== 'forbidden' ? (
            <button aria-busy={invitesApi.loading}
              type="button"
              onClick={() => void invitesApi.refresh()}
              disabled={invitesApi.loading}
              className="shrink-0 font-medium text-danger-soft hover:opacity-80 disabled:opacity-50"
            >
              <ButtonContent loading={invitesApi.loading}>Refresh list</ButtonContent>
            </button>
          ) : null}
        </div>
      )}

      {activeMembersLoading && activeMembers.length === 0 ? (
        <div role="status" className="text-muted-foreground text-sm py-4">
          Loading members...
        </div>
      ) : activeMembersError && activeMembers.length === 0 ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 py-4 text-sm text-danger"
        >
          <span>{activeMembersError}</span>
          <button
            type="button"
            onClick={() => reloadMembersAfterError()}
            className="shrink-0 font-medium hover:opacity-80"
          >
            Load members
          </button>
        </div>
      ) : (
        <>
          {activeMembersError && (
            <div
              role="alert"
              className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-wash-danger px-3 py-2 text-sm text-danger-soft"
            >
              <span>Couldn't refresh members. The list may be out of date.</span>
              <button
                type="button"
                onClick={() => reloadMembersAfterError()}
                className="shrink-0 font-medium text-danger-soft hover:opacity-80"
              >
                Refresh members
              </button>
            </div>
          )}
          {activeMembersLoading && (
            <div role="status" className="mb-4 text-sm text-muted-foreground">
              Refreshing members...
            </div>
          )}

          {/* Members list */}
          <div className="space-y-2 mb-6">
            {activeMembers.map((member) => {
              const primary = member.full_name || member.email || member.user_id;
              const secondary =
                member.full_name && member.email && member.full_name !== member.email
                  ? member.email
                  : null;
              const avatarChar = (primary || '?').charAt(0).toUpperCase();
              const isSelf = member.user_id === user?.id;
              const isOwner = member.role_name === 'owner';
              // Role switch is surfaced only for owner/admin viewers, never on
              // own row, and never on an owner row. The DB still enforces all
              // of these - the UI is just matching the contract.
              const showRoleSelect = canManage && !isSelf && !isOwner;
              const memberRowErr = activeRowError[`member-${member.user_id}`];
              const memberRecoveryId = memberRowErr
                ? `member-${member.user_id}-recovery`
                : undefined;
              const memberMutationsBlocked = activeMembersLoading
                || memberRowErr?.action === 'reload-members';
              return (
                <div
                  key={member.user_id}
                  className="p-3 bg-muted rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-volt/20 text-volt-soft light:text-volt flex items-center justify-center text-sm font-bold shrink-0">
                        {avatarChar}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm text-foreground truncate">
                          {primary}
                          {isSelf && (
                            <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {secondary ? (
                            <>
                              <span className="truncate">{secondary}</span>
                              <span className="mx-1">·</span>
                            </>
                          ) : null}
                          {!showRoleSelect && (
                            <span className="capitalize">{member.role_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {showRoleSelect && (
                        <select
                          data-testid="role-select"
                          value={member.role_id}
                          disabled={memberMutationsBlocked}
                          aria-describedby={memberRecoveryId}
                          onChange={(e) => {
                            const nextId = e.target.value;
                            void handleChangeRole(member.user_id, nextId, member.role_name);
                          }}
                          className="px-2 py-1 bg-surface border border-border rounded-lg text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-volt/50"
                        >
                          {roles
                            .filter((r) => r.name !== 'owner')
                            .map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                        </select>
                      )}
                      {canManage && !isSelf && !isOwner && (
                        <button
                          onClick={() => handleRemoveMember(member.user_id)}
                          disabled={memberMutationsBlocked}
                          aria-describedby={memberRecoveryId}
                          className="p-1.5 text-muted-foreground hover:text-danger transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                          title="Remove member"
                        >
                          <IconTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {memberRowErr && (
                    <div
                      role="alert"
                      className="mt-2 flex items-center justify-between gap-3 text-xs text-danger"
                    >
                      <span id={memberRecoveryId}>{memberRowErr.message}</span>
                      {memberRowErr.action === 'reload-members' && (
                        <button
                          type="button"
                          onClick={() => reloadMembersAfterError(`member-${member.user_id}`)}
                          className="shrink-0 font-medium hover:opacity-80"
                        >
                          Refresh members
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pending invites - filtered through `displayedInvites` so ghost
              invites (already-members) are suppressed from the UI. */}
          {displayedInvites.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Pending Invites</h3>
              <div className="space-y-2">
                {displayedInvites.map((invite: Invite) => {
                  const atCap = invite.send_count >= RESEND_CAP;
                  const inviteRowError = activeRowError[invite.id];
                  const inviteRecoveryId = inviteRowError
                    ? `invite-${invite.id}-recovery`
                    : undefined;
                  const needsReconciliation = inviteRowError?.action === 'refresh-invites';
                  const inviteMutationsBlocked = invitesApi.loading
                    || Boolean(invitesApi.error)
                    || needsReconciliation;
                  const resendDisabled = atCap
                    || inviteMutationsBlocked
                    || inviteRowError?.blockedAction === 'resend'
                    || inviteRowError?.retryAfterMs !== undefined
                    || activeResendingId === invite.id
                    || activeCancelingId === invite.id;
                  const cancelDisabled = inviteMutationsBlocked
                    || inviteRowError?.blockedAction === 'cancel'
                    || activeCancelingId === invite.id
                    || activeResendingId === invite.id;
                  const resendGuidanceId = atCap
                    ? `invite-${invite.id}-resend-cap`
                    : undefined;
                  const resendRecoveryId = inviteRowError?.blockedAction === 'cancel'
                    ? undefined
                    : inviteRecoveryId;
                  const cancelRecoveryId = needsReconciliation
                    || inviteRowError?.blockedAction === 'cancel'
                    ? inviteRecoveryId
                    : undefined;
                  const resendDescription = [resendGuidanceId, resendRecoveryId]
                    .filter((id): id is string => Boolean(id))
                    .join(' ') || undefined;
                  return (
                    <div key={invite.id} className="p-3 bg-surface-raised rounded-lg border border-border/50">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <IconMail className="w-4 h-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm text-muted-foreground truncate">{invite.email}</div>
                            <div className="text-xs text-muted-foreground">
                              <span className="capitalize">{invite.role_name}</span>
                              {' · '}
                              {relativeSent(invite.last_sent_at)}
                              {' · '}
                              Expires {new Date(invite.expires_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-2 shrink-0">
                            <button aria-busy={activeResendingId === invite.id}
                              type="button"
                              onClick={() => handleResend(invite.id)}
                              disabled={resendDisabled}
                              aria-describedby={resendDescription}
                              className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ButtonContent loading={activeResendingId === invite.id}>Resend</ButtonContent>
                            </button>
                            <button aria-busy={activeCancelingId === invite.id}
                              type="button"
                              onClick={() => handleRevokeInvite(invite.id)}
                              disabled={cancelDisabled}
                              aria-describedby={cancelRecoveryId}
                              className="text-xs text-muted-foreground hover:text-danger transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <ButtonContent loading={activeCancelingId === invite.id}>Cancel invite</ButtonContent>
                            </button>
                          </div>
                        )}
                      </div>
                      {atCap && canManage && (
                        <p
                          id={resendGuidanceId}
                          className="mt-2 text-xs text-muted-foreground"
                        >
                          This invite has reached its resend limit. Cancel it, then send a new invite.
                        </p>
                      )}
                      {inviteRowError && (
                        <div
                          role="alert"
                          className="mt-2 flex items-center justify-between gap-3 text-xs text-danger"
                        >
                          <span id={inviteRecoveryId}>{inviteRowError.message}</span>
                          {inviteRowError.action === 'refresh-invites' && (
                            <button
                              type="button"
                              onClick={() => refreshInvitesAfterError(invite.id)}
                              className="shrink-0 font-medium hover:opacity-80"
                            >
                              Refresh list
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Persistent success banner - shown after the form collapses so
              the admin still sees the outcome (esp. the sent:false variant). */}
          {canManage && !activeShowInviteForm && activeInviteSuccess && (
            <p className="text-xs text-success mb-3">{activeInviteSuccess}</p>
          )}

          {/* Invite button / form */}
          {canManage && !invitesApi.loading && !invitesApi.error && (
            <>
              {activeShowInviteForm ? (
                <div className="p-4 bg-muted rounded-lg">
                  <h3 className="text-sm font-medium text-foreground mb-3">Invite a Member</h3>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@example.com"
                      aria-invalid={inviteEmailMatchesMember || undefined}
                      aria-describedby={inviteEmailMatchesMember ? 'invite-existing-member' : undefined}
                      className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-volt/50"
                    />
                    <select
                      value={inviteRoleId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setInviteRoleId(id);
                        const role = roles.find((r) => r.id === id);
                        if (role) setInviteRoleName(role.name);
                      }}
                      className="px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-volt/50"
                    >
                      {roles
                        .filter((r) => r.name !== 'owner') // Can't invite as owner
                        .map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  {!canInvite && (
                    <p id="invite-member-limit" className="text-xs text-warning mb-2">
                      All member seats are in use. Remove a member or cancel a pending invite before sending another.
                    </p>
                  )}
                  {inviteEmailMatchesMember && (
                    <p id="invite-existing-member" className="text-xs text-warning mb-2">
                      This person is already a member.
                    </p>
                  )}
                  {activeInviteError && (
                    <div
                      role="alert"
                      className="mb-2 flex items-center justify-between gap-3 text-xs text-danger"
                    >
                      <span id="invite-action-error">{activeInviteError.message}</span>
                      {activeInviteError.action === 'refresh-invites' && (
                        <button
                          type="button"
                          onClick={() => refreshInvitesAfterError()}
                          className="shrink-0 font-medium hover:opacity-80"
                        >
                          Check pending invites
                        </button>
                      )}
                    </div>
                  )}
                  {activeInviteSuccess && (
                    <p className="text-xs text-success mb-2">{activeInviteSuccess}</p>
                  )}
                  <div className="flex gap-2">
                    <button aria-busy={activeInviting}
                      onClick={handleInvite}
                      disabled={activeInviting
                        || !inviteEmail
                        || !canInvite
                        || inviteEmailMatchesMember
                        || activeInviteError?.blockedAction === 'send'
                        || activeInviteError?.retryAfterMs !== undefined}
                      aria-describedby={inviteSendGuidance}
                      className="rounded-lg px-4 py-2.5 button-accent font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ButtonContent loading={activeInviting}>Send Invite</ButtonContent>
                    </button>
                    <button
                      onClick={() => {
                        clearInviteRetryTimer();
                        setShowInviteForm(false);
                        setInviteError(null);
                        setInviteSuccess(null);
                      }}
                      className="px-4 py-2 text-muted-foreground text-sm hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-surface-hover transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Invite Member
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
